import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { daysUntilDate, formatDateOnly } from "@alm/domain";
import type { RouterOutputs } from "@alm/api-contracts";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportTable, type ReportColumn } from "@/components/app/documents/report-table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { EmployeeFileLink } from "./-components/employee-file-link";

const WINDOWS = [
  { value: 7, label: "Next 7 days" },
  { value: 30, label: "Next 30 days" },
  { value: 60, label: "Next 60 days" },
  { value: 90, label: "Next 90 days" },
] as const;

type Window = (typeof WINDOWS)[number]["value"];

const isWindow = (value: unknown): value is Window =>
  WINDOWS.some((window) => window.value === value);

export const Route = createFileRoute("/_authenticated/_workforce/hr/expiring-documents")({
  /* The window lives in the address, so the overview's "within 60 days" opens
     this report on the same 60 days rather than on a different 30. */
  validateSearch: (search: Record<string, unknown>): { days?: Window } => ({
    days: isWindow(search.days) ? search.days : undefined,
  }),
  component: ExpiringDocuments,
});

/* The same thirty days the badge on an employee's file turns amber at, so a
   renewal reads as urgent in both places or in neither. */
function whenTone(expiresAt: string | null): "destructive" | "warn" | "outline" {
  const days = daysUntilDate(expiresAt);
  if (days === null) return "outline";
  return days < 0 ? "destructive" : days <= 30 ? "warn" : "outline";
}

function whenLabel(expiresAt: string | null): string {
  const days = daysUntilDate(expiresAt);
  if (days === null) return "No expiry recorded";
  if (days < 0) return `Expired ${Math.abs(days)} ${Math.abs(days) === 1 ? "day" : "days"} ago`;
  if (days === 0) return "Expires today";
  return `Expires in ${days} ${days === 1 ? "day" : "days"}`;
}

type ExpiringRow = RouterOutputs["workforce"]["employeeDocuments"]["expiringReport"][number];

const COLUMNS: ReadonlyArray<ReportColumn<ExpiringRow>> = [
  {
    header: "Employee",
    cell: (row) => (
      <PersonCell
        name={row.name || "Unnamed"}
        renderLink={(name) => (
          <EmployeeFileLink userId={row.userId} name={row.name}>
            {name}
          </EmployeeFileLink>
        )}
      />
    ),
    value: (row) => row.name || "Unnamed",
  },
  { header: "Email", cell: (row) => row.email ?? "-" },
  { header: "Document", cell: (row) => row.requirement },
  {
    header: "File",
    cell: (row) => row.fileName ?? "-",
    value: (row) => row.fileName,
  },
  {
    header: "When",
    cell: (row) => <Badge variant={whenTone(row.expiresAt)}>{whenLabel(row.expiresAt)}</Badge>,
    value: (row) => whenLabel(row.expiresAt),
  },
  {
    header: "Expires",
    align: "right",
    cell: (row) => (row.expiresAt ? formatDateOnly(row.expiresAt) : "-"),
    value: (row) => row.expiresAt,
  },
];

/** Accepted documents that lapse inside the chosen window, while there is still time to renew them.
 * Anything already past its date has stopped being a valid document and is counted on the
 * missing-documents report instead. */
function ExpiringDocuments() {
  const { facilityId } = useFacility();
  const { days = 30 } = Route.useSearch();
  const navigate = Route.useNavigate();
  const report = useQuery(
    orpc.workforce.employeeDocuments.expiringReport.queryOptions({
      input: { facilityId, days },
    }),
  );

  const rows = (report.data ?? []).toSorted((left, right) =>
    (left.expiresAt ?? "").localeCompare(right.expiresAt ?? ""),
  );
  const meta = report.isError
    ? "Report unavailable"
    : report.isPending
      ? "Building the report…"
      : rows.length > 0
        ? `${rows.length} ${rows.length === 1 ? "document lapses" : "documents lapse"} within ${days} days`
        : `Accepted documents that lapse within ${days} days`;

  return (
    <>
      <PageHeader
        title="Expiring documents"
        meta={meta}
        actions={
          <Select
            value={String(days)}
            onValueChange={(value) => {
              const next = Number(value);
              if (isWindow(next)) void navigate({ search: { days: next }, replace: true });
            }}
          >
            <SelectTrigger className="w-44" aria-label="Expiring within">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((window) => (
                <SelectItem key={window.value} value={String(window.value)}>
                  {window.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {report.isError ? (
        <ReadFailed
          message="Could not load the expiring-document report. Nothing here means nothing was read, not that nothing lapses."
          error={report.error}
          onRetry={() => void report.refetch()}
          retrying={report.isFetching}
        />
      ) : (
        <ReportTable
          rows={rows}
          pending={report.isPending}
          filename={`expiring-documents-${days}-days.csv`}
          rowKey={(row) => row.requirementId}
          empty={{
            title: "Nothing lapsing in this window",
            description: "No accepted document expires in the selected period.",
          }}
          columns={COLUMNS}
        />
      )}
    </>
  );
}
